import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Lock, UserPlus, RefreshCw } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import {
  currentTenant,
  currentUser,
  gateConfig,
  teamMembers,
  connectedSeats,
  planUsage,
  type GateMode,
  type GateRow,
} from '@/lib/mocks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'
import { Tabs } from '@/components/ui/tabs'
import { PageHeader, Avatar } from '@/lib/atsUi'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/settings')({ component: SettingsPage })

type Tab = 'Automation' | 'Team' | 'Connected seats' | 'Plan & usage'

const TAB_ITEMS: { value: Tab; label: string }[] = [
  { value: 'Automation', label: 'Automation' },
  { value: 'Team', label: 'Team' },
  { value: 'Connected seats', label: 'Connected seats' },
  { value: 'Plan & usage', label: 'Plan & usage' },
]

function SettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('Automation')
  const [gates, setGates] = useState<GateRow[]>(gateConfig)

  function setMode(index: number, mode: GateMode) {
    const row = gates[index]
    if (row.mode === mode) return
    const prevMode = row.mode
    setGates((prev) => prev.map((g, i) => (i === index ? { ...g, mode } : g)))
    toast.info(`${row.agent} › ${row.step} set to ${MODE_LABELS[mode]}`, {
      action: {
        label: 'Undo',
        onClick: () =>
          setGates((prev) => prev.map((g, i) => (i === index ? { ...g, mode: prevMode } : g))),
      },
    })
  }

  return (
    <DashboardLayout title="Settings" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Settings"
        subtitle="Tune how much the agents do on their own — and manage your team and plan."
      />

      <Tabs items={TAB_ITEMS} value={activeTab} onChange={(v) => setActiveTab(v as Tab)} />

      <div className="space-y-6">
        {activeTab === 'Automation' && <AutomationTab gates={gates} onSetMode={setMode} />}
        {activeTab === 'Team' && <TeamTab />}
        {activeTab === 'Connected seats' && <ConnectedSeatsTab />}
        {activeTab === 'Plan & usage' && <PlanUsageTab />}
      </div>
    </DashboardLayout>
  )
}

// ── Tab 1: Automation ──────────────────────────────────────────────────────────

const MODE_STYLES: Record<GateMode, string> = {
  auto: 'bg-success/25 text-success font-semibold',
  review: 'bg-warning/30 text-warning-foreground font-semibold',
  off: 'bg-foreground/10 text-foreground font-semibold',
}
const MODE_LABELS: Record<GateMode, string> = {
  auto: 'AI',
  review: 'Review',
  off: 'Off',
}

interface AutomationTabProps {
  gates: GateRow[]
  onSetMode: (index: number, mode: GateMode) => void
}

function AutomationTab({ gates, onSetMode }: AutomationTabProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose how each step runs: <span className="text-foreground">AI</span> handles it end-to-end,{' '}
        <span className="text-foreground">Review</span> means the AI prepares it for your approval, or{' '}
        <span className="text-foreground">Off</span>.
      </p>
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {gates.map((row, i) => {
              const locked = row.agent === 'RPA'
              return (
                <div
                  key={`${row.agent}-${row.step}`}
                  className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{row.agent}</span>
                      <span className="text-xs text-muted-foreground">›</span>
                      <span className="text-sm text-foreground">{row.step}</span>
                      {locked && <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{row.note}</p>
                  </div>

                  <div
                    className={cn(
                      'inline-flex items-center rounded-md border border-border text-xs font-medium overflow-hidden shrink-0',
                      locked && 'opacity-60'
                    )}
                  >
                    {(['auto', 'review', 'off'] as GateMode[]).map((mode) => (
                      <button
                        key={mode}
                        disabled={locked}
                        onClick={() => onSetMode(i, mode)}
                        className={cn(
                          'px-3 py-1.5 transition-colors',
                          row.mode === mode
                            ? MODE_STYLES[mode]
                            : 'text-muted-foreground hover:text-foreground',
                          !locked && 'cursor-pointer',
                          locked && 'cursor-not-allowed'
                        )}
                      >
                        {MODE_LABELS[mode]}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ── Tab 2: Team ────────────────────────────────────────────────────────────────

function TeamTab() {
  function handleInvite() {
    toast.info('Opening invite dialog…')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">Team members</h2>
        <Button size="sm" onClick={handleInvite}>
          <UserPlus className="h-4 w-4" />
          Invite member
        </Button>
      </div>
      <DataList
        items={teamMembers}
        getKey={(m) => m.id}
        pageSize={5}
        empty="No team members."
        renderRow={(member) => (
          <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
            <Avatar name={member.name} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{member.name}</p>
              <p className="text-xs text-muted-foreground">{member.email}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="info">{member.role}</Badge>
              <Badge variant={member.status === 'active' ? 'success' : 'warning'}>
                {member.status}
              </Badge>
            </div>
          </div>
        )}
      />
    </div>
  )
}

// ── Tab 3: Connected seats ─────────────────────────────────────────────────────

function ConnectedSeatsTab() {
  function handleReconnect(platform: string) {
    toast.info(`Opening login for ${platform}…`)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        You log in once to your own paid seat; we drive it human-paced and never store your password.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        {connectedSeats.map((seat) => (
          <Card key={seat.id}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-foreground">{seat.platform}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{seat.account}</p>
                </div>
                <Badge variant={seat.status === 'connected' ? 'success' : 'warning'}>
                  {seat.status === 'connected' ? 'Connected' : 'Needs login'}
                </Badge>
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Last sync: {seat.lastSync}</span>
                <span>Daily cap: {seat.dailyCap} searches</span>
              </div>

              {seat.status === 'needs_login' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => handleReconnect(seat.platform)}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Re-connect
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

// ── Tab 4: Plan & usage ────────────────────────────────────────────────────────

function UsageMeter({ label, used, total }: { label: string; used: number; total: number }) {
  const pct = Math.min(100, Math.round((used / total) * 100))
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums text-foreground font-medium">
          {used.toLocaleString()} / {total.toLocaleString()}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function PlanUsageTab() {
  return (
    <div className="space-y-6 max-w-lg">
      <div className="space-y-1">
        <h2 className="text-sm font-medium text-muted-foreground">Current plan</h2>
        <p className="text-base font-semibold text-foreground">{planUsage.plan}</p>
        <p className="text-sm text-muted-foreground">
          {planUsage.floor} + {planUsage.successPct}% of placements
        </p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-5">
          <h2 className="text-sm font-medium text-muted-foreground">Usage this month</h2>
          <UsageMeter
            label="Sourcing runs"
            used={planUsage.runsUsed}
            total={planUsage.runsIncluded}
          />
          <UsageMeter
            label="Credits"
            used={planUsage.creditsUsed}
            total={planUsage.creditsIncluded}
          />
        </CardContent>
      </Card>

      <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
        <div>
          <p className="text-xs text-muted-foreground">Bill this month</p>
          <p className="text-lg font-semibold tabular-nums text-foreground">
            {planUsage.billThisMonth}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Placements billed</p>
          <p className="text-lg font-semibold tabular-nums text-foreground">
            {planUsage.placementsBilled}
          </p>
        </div>
      </div>
    </div>
  )
}
